import type { Button } from './input';
import type { HighscoreEntry, Options } from '../storage/storage';

/**
 * Optionen-Screen im LCD-Look (kein Web-Overlay). Navigation per Steuerkreuz:
 * ▲▼ wählt die Zeile, ◀▶ ändert den Wert, A/START/SELECT schließt.
 */
export class OptionsScreen {
  private open_ = false;
  private sel = 0;
  private readonly editableRows = 3;

  constructor(
    private readonly el: HTMLElement,
    private opts: Options,
    private readonly onChange: (o: Options) => void,
    private readonly getHighscores: () => HighscoreEntry[],
  ) {}

  get isOpen(): boolean {
    return this.open_;
  }

  get currentOptions(): Options {
    return this.opts;
  }

  setOptions(o: Options): void {
    this.opts = o;
    if (this.open_) this.render();
  }

  open(): void {
    this.open_ = true;
    this.el.hidden = false;
    this.sel = 0;
    this.render();
  }

  close(): void {
    this.open_ = false;
    this.el.hidden = true;
  }

  toggle(): void {
    if (this.open_) this.close();
    else this.open();
  }

  /** Eingabe im Optionen-Modus. */
  handle(btn: Button): void {
    switch (btn) {
      case 'up':
        this.sel = (this.sel + this.editableRows - 1) % this.editableRows;
        break;
      case 'down':
        this.sel = (this.sel + 1) % this.editableRows;
        break;
      case 'left':
        this.change(-1);
        break;
      case 'right':
        this.change(1);
        break;
      case 'a':
      case 'b':
      case 'start':
      case 'select':
        this.close();
        return;
    }
    this.render();
  }

  private change(dir: number): void {
    const o = { ...this.opts };
    if (this.sel === 0) o.ghost = !o.ghost;
    else if (this.sel === 1) o.startLevel = clamp(o.startLevel + dir, 1, 9);
    else if (this.sel === 2) o.sound = !o.sound;
    this.opts = o;
    this.onChange(o);
  }

  private render(): void {
    const rows: Array<{ label: string; value: string }> = [
      { label: 'GHOST', value: this.opts.ghost ? 'AN' : 'AUS' },
      { label: 'LEVEL', value: String(this.opts.startLevel) },
      { label: 'SOUND', value: this.opts.sound ? 'AN' : 'AUS' },
    ];

    const rowHtml = rows
      .map(
        (r, i) =>
          `<div class="opt-row${i === this.sel ? ' opt-row--active' : ''}">` +
          `<span>${r.label}</span><span>${i === this.sel ? '◀ ' : ''}${r.value}${
            i === this.sel ? ' ▶' : ''
          }</span></div>`,
      )
      .join('');

    const hs = this.getHighscores();
    const hsHtml = hs.length
      ? hs
          .map(
            (h, i) =>
              `<div class="hs-line"><span>${i + 1}. ${String(h.score).padStart(5, '0')}</span>` +
              `<span>LV${h.level}</span></div>`,
          )
          .join('')
      : '<div class="hs-line"><span>—</span><span></span></div>';

    this.el.innerHTML =
      `<div class="options__title">OPTIONEN</div>` +
      rowHtml +
      `<div class="status__divider"></div>` +
      `<div class="status__label">HIGHSCORE</div>` +
      hsHtml +
      `<div class="options__hint">▲▼ WÄHLEN\n◀▶ ÄNDERN\nSTART = ZU</div>`;
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
